import config from "../config/index.js";

/**
 * The four-card "How this loan works" explainer — Source / Interest / Repay
 * / Default risk.
 *
 * §M6 (Jul 2026 client review): "I think we need more information about the
 * loans on this page. Perhaps add this bar at the bottom (it is taken from
 * /loans/50)". It previously lived inline in LoanDetail only; extracted here
 * so the Borrow page can show the same explainer without the copy forking.
 *
 * `liquidationThresholdBps` is passed in by LoanDetail (which reads the
 * per-loan on-chain threshold) and falls back to the protocol default on the
 * generic Borrow surface, where no specific loan exists yet.
 */
export default function HowLoansWork({ liquidationThresholdBps, className = "" }) {
    const liqPct =
        (liquidationThresholdBps ?? config.protocol.liquidationThresholdBps) / 100;
    const loanAprPct = config.protocol.loanAprBps / 100;

    return (
        <div className={`rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 ${className}`}>
            <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                How this loan works
            </h3>
            <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <FlowCard
                    title="Source"
                    body="Capital came from THIS deposit's Safe Vault slice, not from anyone else's. Per-deposit isolation enforced on-chain."
                />
                <FlowCard
                    title="Interest"
                    body={`Your bond keeps earning while borrowed. The contract nets that yield against your ${loanAprPct}% loan interest daily, so the accrued figure is already offset by bond yield. Repayment stays optional while the bond is active.`}
                />
                <FlowCard
                    title="Repay"
                    body="Click Pay Now to settle in a single transaction. The contract auto-computes exact owed (no dust)."
                />
                {/* §M6 — the client asked to understand liquidation "in
                    reality", and specifically that it should be run by the
                    system or an admin rather than a random third party. The
                    contract now enforces that (Lending.restrictedLiquidation
                    + LIQUIDATOR_ROLE), so the copy says who does it. */}
                <FlowCard
                    title="Default risk"
                    body={`No liquidation is possible before the ${config.protocol.fixedTermDays}-day term ends — there is no liquidation path during the term, whatever your LTV. After the term, if LTV reaches ${liqPct}% the protocol's own keeper (or an admin) can liquidate: your outstanding debt is cleared from this deposit's Safe Vault collateral, the liquidation fee goes to the protocol's Ops wallet, and every remaining dollar of collateral is returned to you.`}
                />
            </ul>
        </div>
    );
}

function FlowCard({ title, body }) {
    return (
        <div className="rounded-[10px] bg-white/5 ring-1 ring-[#bfb67b]/30 px-3 py-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#FBC206]">
                {title}
            </div>
            <p className="mt-1 text-[11px] leading-snug text-white/85">{body}</p>
        </div>
    );
}
